/**
 * The council's market policy — what the AI crowns put on the book.
 *
 * The rule `tradeai.ts` states holds here word for word: a system the player
 * can play and the AI cannot is a menu, not a mechanic. Every crown posts and
 * is matched through exactly the `tryPostSell` / `tryPostBuy` / `matchMarket`
 * the player's commands go through. There is no second door and no hidden
 * price.
 *
 * **It draws no random numbers, deliberately** — the same argument, verbatim,
 * as the trade council's: every draw here would shift `w.rngCampaign` for
 * every system downstream, and the market's whole job is to be the most
 * auditable thing on the board. A crown's postings are a pure function of its
 * own treasury and the public book.
 *
 * The policy, in full, because a market the player cannot read is
 * indistinguishable from one that is cheating:
 *
 *   1. **Sell the surplus.** Anything above the keep-floor in `AI_KEEP` goes
 *      up in lots, priced above base and settling toward it as rival sellers
 *      appear (`SELL_SCARCITY`).
 *   2. **Bid for the shortfall.** Anything under its `AI_TARGET` — iron for
 *      musters, timber and stone for hulls and harbours — is bid for below
 *      base, climbing toward and slightly past it as the shortage bites
 *      (`BUY_DESPERATION`), war chest permitting (`AI_GOLD_CUSHION`).
 *   3. **Food before anything.** Grain's floors are not constants: they are the
 *      crown's own monthly bill (`food.ts`) times a number of months, so a
 *      crown sells only what it will not eat this year and bids the moment the
 *      larder falls under a season. Hunger jumps the queue and ignores the war
 *      chest, and a crown that has gone hungry since it posted takes its own
 *      grain back off the book before it bids for anybody else's.
 *   4. **One order per side per good, six per crown.** The book stays legible
 *      and no crown corners it by volume.
 *
 * This file owns the monthly entry point (`marketTurn`) rather than
 * `market.ts`, so the import arrow between the two runs one way — the same
 * shape `bindings.ts` documents as load-bearing rather than tidy.
 */

import type { ResourceKey } from '../types.ts';
import type { CampaignState } from './types.ts';
import { FOOD_BID_MONTHS, FOOD_KEEP_MONTHS, foodBill, foodStores, yardFood } from './food.ts';
import {
  AI_GOLD_CUSHION,
  AI_KEEP,
  AI_MAX_LOT,
  AI_MIN_LOT,
  AI_TARGET,
  BUY_BASE,
  BUY_DESPERATION,
  MARKET_KEYS,
  MARKET_PRICES,
  MAX_LIVE_ORDERS,
  OrderKind,
  SELL_SCARCITY,
} from './markettypes.ts';
import {
  cancelOrder,
  expireOrders,
  hasOrder,
  matchMarket,
  ordersOf,
  sellersOf,
  tryPostBuy,
  tryPostSell,
} from './market.ts';

/**
 * The market's month, at the council: sweep the book, let every crown post,
 * then match — AI orders are on the book *before* matching, so a crown's ask
 * and the player's bid meet in the same sitting they were made in.
 *
 * Called once per turn from the `Council` phase in `turn.ts`, after the
 * military council has spent — what a crown sells is what the month's musters
 * left over, which is the escrow rule doing its work.
 */
export function marketTurn(c: CampaignState): void {
  if (c.over) return;
  expireOrders(c);
  for (let n = 0; n < c.nations.length; n++) {
    if (n === c.playerNation) continue;
    if (!c.nations[n].alive) continue;
    crownPostings(c, n);
  }
  matchMarket(c);
}

/**
 * What a crown will not sell below, and what it is trying to hold.
 *
 * Every good but one is a flat number out of `AI_KEEP` / `AI_TARGET`. **Food is
 * not**, and it cannot be: a crown's grain bill is a function of the ground it
 * holds and the hosts it feeds (see `food.ts`), so a flat target would have a
 * two-province crown hoarding a year's food and a twenty-province crown at war
 * selling the grain it eats next month. Both floors are therefore quoted in
 * *months of the actual bill*, which is the same number the world map's larder
 * panel prints — the AI and the player read one rule.
 *
 * Two details that matter and are easy to get wrong:
 *
 * **The larder is the yards plus the treasury, but only the treasury can be
 * sold.** Grain is carted to city yards and the book escrows out of the
 * national purse, so a crown with full barns and a small reserve is genuinely
 * rich in food and should be selling. The keep floor is therefore the whole
 * larder's requirement *less what is sitting in the yards*, which is what makes
 * a grain-growing crown a real seller instead of a permanent hoarder.
 *
 * **`FOOD_KEEP_MONTHS` is strictly above `FOOD_BID_MONTHS`**, so the sell test
 * and the buy test can never both pass in one sitting. That is the same
 * invariant `AI_KEEP`'s comment states for iron — but a *dynamic* floor needs
 * one thing more than a tabulated one to keep it, which is `recallFood` below.
 */
function keepFloor(c: CampaignState, n: number, key: ResourceKey): number | undefined {
  if (key !== 'grain') return AI_KEEP[key];
  return Math.max(0, foodBill(c, n) * FOOD_KEEP_MONTHS - yardFood(c, n));
}

function buyTarget(c: CampaignState, n: number, key: ResourceKey): number | undefined {
  if (key !== 'grain') return AI_TARGET[key];
  const want = foodBill(c, n) * FOOD_BID_MONTHS - yardFood(c, n);
  return want > 0 ? want : undefined;
}

/**
 * Take our own grain back off the table before we go looking for anybody
 * else's.
 *
 * The floors for every other good are constants, so a crown that was a seller
 * last year is a seller this year and the "never both sides at once" invariant
 * holds for free. **Food's floor moves**, because the bill moves: a crown posts
 * a fair surplus in a quiet spring, raises three hosts and loses its grain
 * province by the autumn, and now has an ask standing on the book for exactly
 * the grain it is starving without — for up to `ORDER_TTL_MONTHS`. Measured
 * across thirty councils, both sides of the grain book at once was common.
 *
 * The escrow comes home in full (`cancelOrder`), so this is also the *fastest*
 * food a hungry crown can get: its own, back out of the shop window, this
 * council, before it bids a coin for anybody else's. It runs before the sell
 * loop so the returned grain is counted when the surplus is worked out, which
 * is what stops a crown cancelling and re-posting the same lot forever.
 */
function recallFood(c: CampaignState, n: number): void {
  if (foodStores(c, n) >= foodBill(c, n) * FOOD_BID_MONTHS) return;
  for (const o of ordersOf(c, n)) {
    if (o.kind === OrderKind.Sell && o.key === 'grain') cancelOrder(c, n, o.id);
  }
}

/** `MARKET_KEYS` with food at the head. Stable, so a replay bids in one order. */
const BUY_ORDER: readonly ResourceKey[] = ['grain', ...MARKET_KEYS.filter((k) => k !== 'grain')];

/**
 * One crown's postings for the month, off its own treasury and the book.
 *
 * Exported so a test can ask what a crown *posts*, which is not the same
 * question as what is left on the book after `matchMarket` has been through it
 * — a bid that fills the instant it is made leaves no trace of the policy that
 * made it.
 */
export function crownPostings(c: CampaignState, n: number): void {
  const t = c.nations[n].treasury;
  recallFood(c, n);
  let slots = MAX_LIVE_ORDERS - ordersOf(c, n).length;

  // 1. Sell the surplus. Walked in MARKET_KEYS order, so it is stable.
  for (const key of MARKET_KEYS) {
    if (slots <= 0) return;
    const keep = keepFloor(c, n, key);
    if (keep === undefined) continue;
    if (hasOrder(c, n, OrderKind.Sell, key)) continue;
    const surplus = Math.floor(t[key] - keep);
    if (surplus < AI_MIN_LOT) continue;
    const lot = Math.min(surplus, AI_MAX_LOT);
    const rivals = sellersOf(c, key, n);
    const ask = Math.max(1, Math.round(MARKET_PRICES[key]! * (1 + SELL_SCARCITY / (1 + rivals))));
    if (tryPostSell(c, n, key, lot, ask)) slots--;
  }

  /**
   * 2. Bid for the shortfall, war chest permitting — **food first**.
   *
   * `MAX_LIVE_ORDERS` is six and the sells above have already spent some of
   * them, so the order this loop walks in decides what a crown at its limit
   * bids for at all. Grain sits fifth in `RESOURCE_KEYS`, behind timber, planks,
   * stone and iron, and the crowns that need grain most are exactly the
   * land-poor ones with hills full of iron to sell — so on the natural walk a
   * starving crown spent every slot it had on its own surplus and its iron want
   * and never got as far as bread. Hunger outranks the war chest above; it
   * outranks the queue here, for the same reason.
   */
  for (const key of BUY_ORDER) {
    if (slots <= 0) return;
    const target = buyTarget(c, n, key);
    if (target === undefined) continue;
    if (hasOrder(c, n, OrderKind.Buy, key)) continue;
    /**
     * The war chest — except in front of bread.
     *
     * `AI_GOLD_CUSHION` exists so the market can never be the reason a crown
     * cannot raise a host. Applied to food it does the opposite: the crowns that
     * most need to buy grain are the land-poor ones, who are also the broke
     * ones, so a hundred and forty gold of cushion is precisely the rule that
     * stops a starving crown ever bidding — measured, it was most of why the
     * grain book still would not clear. A crown with nothing to eat has no use
     * for a war chest.
     */
    if (key !== 'grain' && t.gold < AI_GOLD_CUSHION) continue;
    const shortfall = Math.round(target - t[key]);
    if (shortfall < 1) continue;
    const want = Math.min(shortfall, AI_MAX_LOT);
    const desperation = Math.max(0, Math.min(1, 1 - t[key] / target));
    const bid = Math.max(
      1,
      Math.round(MARKET_PRICES[key]! * (BUY_BASE + BUY_DESPERATION * desperation)),
    );
    if (tryPostBuy(c, n, key, want, bid)) slots--;
  }
}
